import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://smilingrootsfl.com";

/**
 * Injects a Product JSON-LD schema into <head> so Google can render rich results
 * (price, availability, star rating, review count) on the product page.
 * Fetches aggregate review data from /api/reviews?product_id=... at mount.
 */
export default function ProductJsonLd({ product }) {
  useEffect(() => {
    if (!product) return;

    const scriptId = "product-jsonld";
    let cancelled = false;

    async function buildAndInject() {
      let average = null;
      let count = 0;
      let reviews = [];
      try {
        const { data } = await axios.get(`${API}/reviews`, {
          params: { product_id: product.id, limit: 20 },
        });
        average = data.average ?? null;
        count = data.count ?? 0;
        reviews = data.reviews || [];
      } catch (_e) {
        // Non-fatal — schema still valid without review aggregate
      }
      if (cancelled) return;

      const productUrl = `${SITE_URL}/product/${product.id}`;
      const imageUrl = product.image?.startsWith("http")
        ? product.image
        : `${SITE_URL}${product.image?.startsWith("/") ? "" : "/"}${product.image || ""}`;

      const schema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: product.name,
        image: [imageUrl],
        description: product.long_description || product.description || product.name,
        sku: product.lookup_key || product.id,
        mpn: product.lookup_key || product.id,
        brand: {
          "@type": "Brand",
          name: "The Smiling Roots",
        },
        offers: {
          "@type": "Offer",
          url: productUrl,
          priceCurrency: "USD",
          price: Number(product.price).toFixed(2),
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          seller: {
            "@type": "Organization",
            name: "The Smiling Roots",
          },
        },
      };

      if (average && count > 0) {
        schema.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: Number(average).toFixed(1),
          reviewCount: count,
          bestRating: "5",
          worstRating: "1",
        };
      }

      // Include up to the 5 most recent reviews inline for Google
      const topReviews = reviews
        .filter((r) => r.rating && r.text)
        .slice(0, 5)
        .map((r) => ({
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: String(r.rating),
            bestRating: "5",
            worstRating: "1",
          },
          author: {
            "@type": "Person",
            name: r.author || "Verified customer",
          },
          reviewBody: r.text,
          ...(r.created_at ? { datePublished: r.created_at.slice(0, 10) } : {}),
        }));
      if (topReviews.length > 0) schema.review = topReviews;

      // Remove any previous tag from an earlier product route
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();

      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
    }

    buildAndInject();

    return () => {
      cancelled = true;
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [product]);

  return null;
}
